import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import PrivateRoute from 'components/common/Auth/Private';
import { PageWrapper } from 'components/common/layout/PageWrapper/PageWrapper';
import { ChildRoute } from 'components/common/types/Route.types';
import DealsHeader from 'components/pages/Deals/DealsHeader';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { checkFeaturePermission } from 'utils/checkFeaturePermission';
import ProgrammaticDeals from './ProgrammaticDeals';
import DirectSalesDeals from './DirectSalesDeals';
import Products from '../PCM/Products';

const Deals: React.FC = () => {
  const arePermissionsEnabled = useHasFeatureAccess(FeatureFlags.PLANNERS_PERMISSIONS);
  const hasDirectSalesAccess = useHasFeatureAccess(FeatureFlags.DIRECT_SALES_PLANNER);

  const hasProgrammaticPermission = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION),
  );

  const hasDirectSalesPermission =
    hasDirectSalesAccess &&
    checkFeaturePermission(arePermissionsEnabled, Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION));

  const hasProductManagementAccess = Auth.hasPermission(PermissionsEnum.PRODUCT_MANAGEMENT_PAGE_ACCESS);

  const childRoutes: ChildRoute[] = [
    ...(hasProgrammaticPermission
      ? [
          {
            id: 1,
            path: `programmatic`,
            main: ProgrammaticDeals,
            pageAccessPermission: undefined,
          },
        ]
      : []),
    ...(hasDirectSalesAccess && hasDirectSalesPermission
      ? [
          {
            id: 2,
            path: `direct-sales`,
            main: DirectSalesDeals,
            pageAccessPermission: undefined,
          },
        ]
      : []),
    ...(hasProductManagementAccess && hasDirectSalesAccess
      ? [
          {
            id: 3,
            path: `product-management`,
            main: Products,
            pageAccessPermission: undefined,
          },
        ]
      : []),
    {
      id: 4,
      path: '*',
      main: () => {
        if (hasProgrammaticPermission) return <Navigate to="programmatic" replace />;
        if (hasDirectSalesPermission) return <Navigate to="direct-sales" replace />;
        return <Navigate to="/accessdenied" replace />;
      },
      pageAccessPermission: undefined,
    },
  ];

  return (
    <PageWrapper>
      <DealsHeader />
      <Routes>
        {childRoutes.map((route) => (
          <Route
            key={route.id}
            path={route.path}
            element={<PrivateRoute component={route.main} pageAccessPermission={route.pageAccessPermission} />}
          />
        ))}
      </Routes>
    </PageWrapper>
  );
};

export default Deals;
